import React from 'react';

import { useSnackbar } from 'notistack';

import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Typography } from '@mui/material';

import { useDeactivateAchievement } from '~/hooks/apis/agent/useDeactivateAchievement';
import { useGetAchievementSubject } from '~/hooks/apis/agent/useGetAchievementSubject';

import { AchievementListRow } from '~/types/services/agentHub';

interface Props {
  achievement: AchievementListRow | undefined;
  open: boolean;
  onClose: () => void;
}

function DeactivateAchievementDialogContent({
  achievement,
  onClose,
}: {
  achievement: AchievementListRow;
  onClose: () => void;
}) {
  const { enqueueSnackbar } = useSnackbar();
  const { data: subject } = useGetAchievementSubject(achievement.subjectType, achievement.subjectId);

  const deactivateMutation = useDeactivateAchievement({
    onSuccess: () => {
      enqueueSnackbar('Achievement deactivated', { variant: 'success' });
    },
    onError: (error) => {
      enqueueSnackbar(error?.message || 'Unable to deactivate achievement', { variant: 'error' });
    },
  });

  const subjectLabel = achievement.subjectType;
  const subjectName = subject?.name?.trim();
  const subjectDescription = subjectName ? `${subjectLabel} ${subjectName}` : `${subjectLabel}`;

  const handleConfirm = () => {
    deactivateMutation.mutate(achievement.id, {
      onSuccess: () => {
        onClose();
      },
    });
  };

  const isPending = deactivateMutation.isPending;

  return (
    <>
      <DialogTitle>Deactivate achievement?</DialogTitle>
      <DialogContent>
        <Typography>
          Are you sure you want to deactivate this achievement for reward &quot;{achievement.rewardName}&quot;? This
          will revoke the benefit for the selected {subjectDescription}.
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} variant="text" disabled={isPending}>
          Cancel
        </Button>
        <Button onClick={handleConfirm} variant="contained" color="primary" disabled={isPending}>
          {isPending ? 'Deactivating...' : 'Deactivate'}
        </Button>
      </DialogActions>
    </>
  );
}

export default function DeactivateAchievementDialog({ achievement, open, onClose }: Props) {
  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      {achievement ? <DeactivateAchievementDialogContent achievement={achievement} onClose={onClose} /> : null}
    </Dialog>
  );
}
